/**
 * `POST /stories/{id}/turns` — the one request that costs money.
 *
 * It is not in `api/client.ts` on purpose (D4): it is a `POST` that answers with
 * `text/event-stream`, it is not idempotent, it runs for tens of seconds, and **it is never
 * retried automatically anywhere in this client**. What it shares with the REST surface is
 * the `Authorization` header, the 401 transition and the error parser — and nothing else.
 *
 * Three properties are worth stating because each one is a way this could be quietly wrong:
 *
 * 1. **`Idempotency-Key` on every attempt** (P2). A double-tapped button, a StrictMode
 * double-effect or a retried request would otherwise be a second $0.13 turn and a
 * duplicate chapter in a story that cannot be un-corrupted. The caller owns the key so
 * that a *deliberate* retry of the same action can reuse it and get a replay.
 * 2. **The client is never the first to give up.** `TURN_CLIENT_TIMEOUT_MS` sits above the
 * server's `TURN_TIMEOUT_SECONDS` (600), which itself sits below the ALB's 900. A client
 * that timed out first would abandon a turn the server is about to commit.
 * 3. **An unknown event name is ignored, never fatal.** A backend that adds an event must
 * not break a deployed client (D5's forward-compatibility half).
 */
import {
  ApiError,
  apiErrorFromDetail,
  apiErrorFromResponse,
  authHeader,
  apiBaseUrl,
} from '@/api/client';
import { isTerminalEvent, isTurnEventName, type TurnEvent } from '@/api/events';
import type { ChapterEvent } from '@/api/events';
import { decodeSseFrames, streamChunks } from '@/turn/sseParser';

/**
 * Above the server's 600s ceiling (`TURN_TIMEOUT_SECONDS`) and below the ALB's 900s idle
 * timeout. Reaching it means the server never wrote its own terminal `error` — i.e. the
 * connection is wedged, not the turn.
 */
export const TURN_CLIENT_TIMEOUT_MS = 660_000;

export interface RunTurnInput {
  storyId: string;
  /** `null` is the opening turn, legal only at `chapter_count == 0`. */
  action: string | null;
  /** P2. Generate with `newIdempotencyKey()`; reuse it to retry *the same* action. */
  idempotencyKey: string;
  /** Every parsed event, in arrival order. The store's `apply` goes here. */
  onEvent: (event: TurnEvent) => void;
  /** Every byte that arrives, **including the `: ping` heartbeat** — stall detection (D4). */
  onActivity?: (at: number) => void;
  signal?: AbortSignal;
  timeoutMs?: number;
}

/**
 * Resolves with the terminal `chapter` event, or throws:
 *
 * | Throw | Meaning for the player |
 * |---|---|
 * | `ApiError` from a non-2xx | the turn never started — 409 `turn_in_progress`, 404, 503 |
 * | `ApiError` from the terminal `error` event | the turn aborted server-side and wrote nothing |
 * | `ApiError('stream_interrupted')` | the turn is *probably still running*; refetch, do not resubmit |
 * | `DOMException` (`AbortError`) | the caller's own `signal` fired |
 */
export async function runTurn(
  input: RunTurnInput,
): Promise<Extract<TurnEvent, { event: 'chapter' }>> {
  const { storyId, action, idempotencyKey, onEvent, onActivity, signal } = input;

  const timeout = new AbortController();
  const timer = setTimeout(() => {
    timeout.abort(new DOMException('The turn stream exceeded the client timeout.', 'TimeoutError'));
  }, input.timeoutMs ?? TURN_CLIENT_TIMEOUT_MS);

  try {
    const response = await fetchTurn({ storyId, action, idempotencyKey, signal, timeout });
    if (!response.ok) throw await apiErrorFromResponse(response);
    if (!response.body) {
      throw new ApiError('malformed_response', 'The turn stream arrived without a body.', {
        status: response.status,
        requestId: response.headers.get('X-Request-Id'),
      });
    }
    return await consume(response, { onEvent, onActivity, signal });
  } finally {
    clearTimeout(timer);
  }
}

async function fetchTurn(args: {
  storyId: string;
  action: string | null;
  idempotencyKey: string;
  signal: AbortSignal | undefined;
  timeout: AbortController;
}): Promise<Response> {
  const url = `${apiBaseUrl()}/stories/${encodeURIComponent(args.storyId)}/turns`;
  const signal = args.signal
    ? AbortSignal.any([args.signal, args.timeout.signal])
    : args.timeout.signal;

  try {
    return await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'text/event-stream',
        'Content-Type': 'application/json',
        // P2. The header is required by the API — there is no unkeyed turn.
        'Idempotency-Key': args.idempotencyKey,
        ...authHeader(),
      },
      body: JSON.stringify({ action: args.action }),
      // A cached turn would be a chapter served twice; say so rather than trust defaults.
      cache: 'no-store',
      signal,
    });
  } catch (cause) {
    throw streamFailure(cause, args.signal, 'The turn could not be started.');
  }
}

async function consume(
  response: Response,
  handlers: {
    onEvent: (event: TurnEvent) => void;
    onActivity?: (at: number) => void;
    signal: AbortSignal | undefined;
  },
): Promise<Extract<TurnEvent, { event: 'chapter' }>> {
  const body = response.body;
  if (!body) throw new ApiError('stream_interrupted', 'The turn stream closed immediately.');

  let terminal: TurnEvent | null = null;

  try {
    for await (const frame of decodeSseFrames(streamChunks(body))) {
      // Before the `kind` check: a heartbeat is not an event, and it is exactly the byte
      // that proves the server is alive through a silent Phase A.
      handlers.onActivity?.(Date.now());
      if (frame.kind === 'comment') continue;

      const event = parseEvent(frame.event, frame.data);
      if (!event) continue;

      handlers.onEvent(event);
      if (isTerminalEvent(event)) {
        terminal = event;
        // Exactly one terminal event ends every stream. Break so the reader is
        // cancelled and the connection released rather than held open to EOF.
        break;
      }
    }
  } catch (cause) {
    throw streamFailure(cause, handlers.signal, 'The turn stream ended early.');
  }

  if (terminal?.event === 'chapter') return terminal;
  if (terminal?.event === 'error') throw apiErrorFromDetail(terminal.data.error);

  // The stream closed with no terminal event: a proxy cut it, the task was killed, the
  // network went away. The turn itself is very likely still running and will commit.
  throw new ApiError('stream_interrupted', 'The turn stream ended without a result.');
}

function parseEvent(name: string | null, data: string): TurnEvent | null {
  if (name === null) {
    console.warn('[turn] ignoring an SSE frame with no event name');
    return null;
  }
  if (!isTurnEventName(name)) {
    // Forward compatibility: a backend that adds an event must not break this client.
    console.warn(`[turn] ignoring unknown event "${name}"`);
    return null;
  }
  try {
    return { event: name, data: JSON.parse(data) as never };
  } catch {
    console.warn(`[turn] ignoring "${name}": payload is not JSON`);
    return null;
  }
}

/**
 * A failed `fetch`, a killed connection and the client timeout all arrive here. The caller's
 * own abort is re-thrown untouched — it is not a failure, it is the caller's instruction.
 */
function streamFailure(cause: unknown, signal: AbortSignal | undefined, message: string): unknown {
  if (signal?.aborted) return cause;
  if (cause instanceof ApiError) return cause;
  return new ApiError('stream_interrupted', message);
}

/**
 * P2's key. `crypto.randomUUID` needs a secure context, which `http://localhost` is — but a
 * LAN-address dev server or an older jsdom is not, and an idempotency key that throws on
 * generation would take the whole turn with it.
 */
export function newIdempotencyKey(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();

  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export type { ChapterEvent };
